/**
 * A Control Panel row's status as the list shows it: "Scheduled" before an
 * In Progress pour starts, else its own status, with the soft tinted pill
 * colours and the lifecycle icon that goes with it.
 *
 * Copies of pages/Pours.tsx's isScheduledPour / displayStatus /
 * getStatusPillSx / getStatusIcon / formatStatusLabel, so the desktop file
 * stays untouched; the phone Control Panel reads them from here.
 */
import type { Pour } from '../../services/pours';
import type { TranslateFn } from '../../utils/relativeTime';

type StatusFields = Pick<Pour, 'PourStatus' | 'Scheduled'>;

/** Whether the pour shows as Scheduled: In Progress (Status stays '1' for
 *  legacy) but not started yet. */
export const isScheduledPour = (pour: StatusFields): boolean =>
  !!pour.Scheduled && (pour.PourStatus ?? '').toLowerCase() === 'in progress';

/** The status shown for a pour: "Scheduled" before its start, else its own. */
export const displayStatus = (pour: StatusFields): string =>
  isScheduledPour(pour) ? 'Scheduled' : pour.PourStatus || '';

/** In Progress, Scheduled included (Stop applies). */
export const isInProgressStatus = (status?: string | null): boolean =>
  (status ?? '').trim().toLowerCase() === 'in progress';

/** Complete / Completed. */
export const isCompletedStatus = (status?: string | null): boolean => {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'complete' || s === 'completed';
};

export const isPendingStatus = (status?: string | null): boolean =>
  (status ?? '').trim().toLowerCase() === 'pending';

/** Soft tinted pill colours for the card status chip (matches the mock). */
export const getStatusPillSx = (status: string): { bgcolor: string; color: string } => {
  switch (status?.toLowerCase()) {
    case 'complete':
    case 'completed':
      return { bgcolor: '#E1F5EE', color: '#0f6e56' };
    case 'in progress':
      return { bgcolor: '#FAEEDA', color: '#9a6a00' };
    case 'pending':
    case 'scheduled':
      return { bgcolor: '#E6F1FB', color: '#185FA5' };
    default:
      return { bgcolor: 'grey.100', color: 'text.secondary' };
  }
};

/** The lifecycle icon paired with the status word, so the state is not
 *  shown by colour alone: sync = running, calendar = scheduled, clock =
 *  pending, tick = complete. null for anything else. */
export type StatusIconKey = 'inProgress' | 'scheduled' | 'pending' | 'complete';

export const statusIconKey = (status: string): StatusIconKey | null => {
  switch (status?.toLowerCase()) {
    case 'complete':
    case 'completed':
      return 'complete';
    case 'in progress':
      return 'inProgress';
    case 'pending':
      return 'pending';
    case 'scheduled':
      return 'scheduled';
    default:
      return null;
  }
};

/** Display label for a pour status: the DB stores 'Complete' / 'In
 *  Progress', the UI shows 'Completed' / 'In Progress'. */
export const formatStatusLabel = (status: string, t: TranslateFn): string => {
  switch (status?.toLowerCase()) {
    case 'complete':
    case 'completed':
      return t('pours.complete', 'Completed');
    case 'in progress':
      return t('pours.inProgress', 'In Progress');
    case 'pending':
      return t('dashboard.pending', 'Pending');
    case 'archived':
      return t('pours.archived', 'Archived');
    case 'scheduled':
      return t('pours.scheduled', 'Scheduled');
    default:
      return status;
  }
};

export interface StatusPill {
  /** displayStatus: 'Scheduled' or the pour's own status. */
  status: string;
  label: string;
  icon: StatusIconKey | null;
  sx: { bgcolor: string; color: string };
}

/** Everything the status pill draws for a row. */
export const statusPill = (pour: StatusFields, t: TranslateFn): StatusPill => {
  const status = displayStatus(pour);
  return {
    status,
    label: status ? formatStatusLabel(status, t) : t('common.unknown', 'Unknown'),
    icon: statusIconKey(status),
    sx: getStatusPillSx(status),
  };
};
